import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
  Chip,
  Avatar,
  Divider,
  Fade,
  Slide,
  useTheme,
  alpha,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { 
  Close, 
  CloudUpload, 
  ArrowBack, 
  Publish, 
  Image as ImageIcon,
  LocationOn,
  AttachMoney,
  Category,
  Description,
  Inventory,
  CheckCircle,
  RadioButtonUnchecked,
  ArrowForward,
  Payment,
  AccountBalance,
  Save
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { userApiLoader } from "../../../action/loaderActions";
import axiosInstance from "../../../utils/axios/axiosInstance";
import { useNavigate, useParams } from "react-router-dom";
import loadingGif from '../../../assets/images/loading.gif';

// Professional Loading Overlay Component
const LoadingOverlay = ({ isLoading, isPaymentCompleted, isDraftSaving }) => {
  if (!isLoading) return null;

  return (
    <Box
      position="fixed"
      top={0}
      left={0}
      right={0}
      bottom={0}
      bgcolor="rgba(0, 0, 0, 0.8)"
      display="flex"
      alignItems="center"
      justifyContent="center"
      zIndex={9999}
      sx={{
        backdropFilter: 'blur(4px)',
        fontFamily: '"Saira", sans-serif',
      }}
    >
      <Card 
        elevation={12}
        sx={{ 
          p: 6, 
          borderRadius: 2,
          backgroundColor: 'white',
          border: '1px solid #e0e0e0',
          textAlign: 'center',
          maxWidth: 400
        }}
      >
        <img
          src={loadingGif}
          alt="Loading..."
          style={{ width: 48, height: 48 }}
        />
        <Typography 
          variant="h6" 
          sx={{ 
            mt: 3, 
            fontWeight: 600,
            color: '#000',
            fontFamily: '"Saira", sans-serif'
          }}
        >
          {isDraftSaving ? "Moving to draft..." : isPaymentCompleted ? "Updating paid listing..." : "Processing your request..."}
        </Typography>
        <Typography 
          variant="body2" 
          sx={{ 
            mt: 1, 
            color: '#666',
            fontFamily: '"Saira", sans-serif'
          }}
        >
          {isDraftSaving ? 
            "Moving your listing to draft..." : 
            isPaymentCompleted ? 
              "Updating your paid listing..." : 
              "Please wait while we update your listing"
          }
        </Typography>
      </Card>
    </Box>
  );
};

// Professional Stepper Component
const ProfessionalStepper = ({ activeStep, steps }) => {
  return (
    <Box sx={{ width: '100%', mb: 4 }}>
      <Stepper activeStep={activeStep} alternativeLabel>
        {steps.map((label, index) => {
          const isActive = index === activeStep;
          const isCompleted = index < activeStep;
          
          return (
            <Step key={label}>
              <StepLabel
                StepIconComponent={({ active, completed }) => (
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: completed || active ? '#ce55b9' : '#e0e0e0',
                      color: completed || active ? 'white' : '#666',
                      fontWeight: 600,
                      fontSize: '14px',
                      fontFamily: '"Saira", sans-serif'
                    }}
                  >
                    {completed ? <CheckCircle sx={{ fontSize: 18 }} /> : index + 1}
                  </Box>
                )}
                sx={{
                  '& .MuiStepLabel-label': {
                    fontFamily: '"Saira", sans-serif',
                    fontWeight: isActive ? 600 : 400,
                    color: isActive ? '#ce55b9' : '#666',
                    fontSize: '14px',
                    mt: 1
                  }
                }}
              >
                {label}
              </StepLabel>
            </Step>
          );
        })}
      </Stepper>
    </Box>
  );
};

const EditAdPage = () => {
  const { id } = useParams(); // Get ad ID from URL params
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [activeStep, setActiveStep] = useState(0);
  const [adData, setAdData] = useState({
    name: "",
    description: "",
    price: "",
    category_id: "",
    sub_category_id: "",
    condition: "",
    shipping_method: "",
    payment_method_type: "online", // Add this new field: "online" or "cash"
    venmo_username: "",        // Updated from payment_option
    paypal_username: "",       // Updated from payment_id
    pickup_location: "",
    thumbnail: null,
    image: [],
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [selectedImageFiles, setSelectedImageFiles] = useState([]);
  const [imagePreviewUrls, setImagePreviewUrls] = useState([]);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [isPaymentCompleted, setIsPaymentCompleted] = useState(false);
  const [isDraftSaving, setIsDraftSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [subcategoriesLoading, setSubcategoriesLoading] = useState(false);
  
  // NEW: Add state to track if we're in the process of loading initial data
  const [isInitialDataLoaded, setIsInitialDataLoaded] = useState(false);
  const [pendingAdData, setPendingAdData] = useState(null);

  const { accessToken } = useSelector((state) => state.user);
  const { isApiLoader } = useSelector((state) => state.loader || { isApiLoader: false });

  const steps = ['Basic Information', 'Category & Details', 'Images & Media'];

  // Utility function to parse API responses consistently
  const parseApiResponse = (response, expectedDataType = 'array') => {
    if (!response || !response.data) {
      return [];
    }

    const data = response.data;
    let extractedData = [];

    // Try different response patterns
    const patterns = [
      // Pattern 1: { status: 200, data: [...] } or { status: 200, data: { categories: [...] } }
      () => {
        if (data.status === 200 && data.data) {
          if (Array.isArray(data.data)) {
            return data.data;
          } else if (data.data.categories && Array.isArray(data.data.categories)) {
            return data.data.categories;
          } else if (data.data.subcategories && Array.isArray(data.data.subcategories)) {
            return data.data.subcategories;
          }
        }
        return null;
      },
      // Pattern 2: { success: true, data: [...] }
      () => {
        if (data.success === true && data.data) {
          if (Array.isArray(data.data)) {
            return data.data;
          } else if (data.data.categories && Array.isArray(data.data.categories)) {
            return data.data.categories;
          } else if (data.data.subcategories && Array.isArray(data.data.subcategories)) {
            return data.data.subcategories;
          }
        }
        return null;
      },
      // Pattern 3: { data: [...] }
      () => {
        if (data.data && Array.isArray(data.data)) {
          return data.data;
        }
        return null;
      },
      // Pattern 4: Direct array response
      () => {
        if (Array.isArray(data)) {
          return data;
        }
        return null;
      },
      // Pattern 5: { categories: [...] } or { subcategories: [...] }
      () => {
        if (data.categories && Array.isArray(data.categories)) {
          return data.categories;
        } else if (data.subcategories && Array.isArray(data.subcategories)) {
          return data.subcategories;
        }
        return null;
      }
    ];

    // Try each pattern until we find data
    for (const pattern of patterns) {
      try {
        const result = pattern();
        if (result && Array.isArray(result)) {
          extractedData = result;
          break;
        }
      } catch (error) {
        console.warn('Error trying pattern:', error);
      }
    }

    // Validate and clean the extracted data
    if (Array.isArray(extractedData)) {
      extractedData = extractedData.filter(item => 
        item && 
        item.id && 
        item.name && 
        typeof item.name === 'string' &&
        item.name.trim() !== ''
      );
    }

    return extractedData || [];
  };

  // Retry function for API calls
  const retryApiCall = async (apiCall, maxRetries = 2, delay = 1000) => {
    let lastError;
    
    for (let i = 0; i <= maxRetries; i++) {
      try {
        const result = await apiCall();
        return result;
      } catch (error) {
        lastError = error;
        
        if (i < maxRetries) {
          console.log(`API call failed, retrying in ${delay}ms... (attempt ${i + 1}/${maxRetries})`);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }
    
    throw lastError;
  };

  // Professional theme styles
  const professionalTheme = {
    fontFamily: '"Saira", sans-serif',
    primary: '#ce55b9',
    black: '#000000',
    white: '#ffffff',
    gray: {
      50: '#fafafa',
      100: '#f5f5f5',
      200: '#eeeeee',
      300: '#e0e0e0',
      400: '#bdbdbd',
      500: '#9e9e9e',
      600: '#757575',
      700: '#616161',
      800: '#424242',
      900: '#212121'
    }
  };

  // Updated validation rules
  const validationRules = {
    name: {
      required: true,
      maxLength: 255,
      message: "Listing title is required and cannot exceed 255 characters"
    },
    description: {
      required: true,
      message: "Description is required"
    },
    price: {
      required: true,
      min: 1,
      isInteger: true,
      message: "Price is required and must be a whole number (integer)"
    },
    category_id: {
      required: true,
      message: "Category selection is required"
    },
    sub_category_id: {
      required: true,
      message: "Subcategory selection is required"
    },
    condition: {
      required: true,
      validValues: [1, 2, 3, 4, 5],
      message: "Item condition selection is required"
    },
    shipping_method: {
      required: true,
      validValues: [1, 2],
      message: "Shipping method selection is required"
    },
    pickup_location: {
      conditionallyRequired: true,
      condition: (formData) => formData.shipping_method === "1" || formData.shipping_method === 1,
      maxLength: 255,
      message: "Pickup location is required when shipping method is pickup only"
    },
    payment_method_type: {
      required: true,
      validValues: ["online", "cash"],
      message: "Payment method type selection is required"
    },
    // Updated payment methods validation
    payment_methods: {
      custom: true,
      validator: (formData) => {
        if (formData.payment_method_type === "cash") {
          return true; // Cash only is always valid
        }
        // For online payments, require at least one payment method
        const hasVenmo = formData.venmo_username && formData.venmo_username.trim() !== "";
        const hasPayPal = formData.paypal_username && formData.paypal_username.trim() !== "";
        return hasVenmo || hasPayPal;
      },
      message: "For online payments, at least one payment method (Venmo or PayPal) is required"
    }
  };

  // Validation functions
  const shouldShowPickupLocation = () => {
    return adData.shipping_method === "1" || adData.shipping_method === 1;
  };

  const validateField = (fieldName, value) => {
    const rules = validationRules[fieldName];
    if (!rules) return "";

    if (!touched[fieldName]) return "";

    // Handle custom validation for payment methods
    if (rules.custom && rules.validator) {
      const isValid = rules.validator(adData);
      if (!isValid) {
        return rules.message;
      }
      return "";
    }

    if (rules.conditionallyRequired && rules.condition) {
      const isRequired = rules.condition(adData);
      if (isRequired) {
        const isEmpty = !value || value === "" || value === null || value === undefined;
        if (isEmpty) {
          return rules.message;
        }
      } else {
        return "";
      }
    } else if (rules.required) {
      const isEmpty = !value || value === "" || value === null || value === undefined;
      if (isEmpty) {
        return rules.message;
      }
    }

    const isEmpty = !value || value === "" || value === null || value === undefined;
    if (!isEmpty) {
      if (rules.maxLength && String(value).length > rules.maxLength) {
        return `This field cannot exceed ${rules.maxLength} characters`;
      }

      if (rules.min !== undefined || rules.max !== undefined) {
        const numValue = parseFloat(value);
        if (isNaN(numValue)) {
          return `Please enter a valid number`;
        }
        if (rules.min !== undefined && numValue < rules.min) {
          return `Value must be at least ${rules.min}`;
        }
      }

      // Additional validation for category and subcategory
      if (fieldName === 'category_id' && categories.length > 0) {
        const categoryExists = categories.some(cat => String(cat.id) === String(value));
        if (!categoryExists) {
          return "Please select a valid category";
        }
      }

      if (fieldName === 'sub_category_id' && subCategories.length > 0) {
        const subCategoryExists = subCategories.some(subCat => String(subCat.id) === String(value));
        if (!subCategoryExists) {
          return "Please select a valid subcategory";
        }
      }
    }

    return "";
  };

  const validateAllFields = () => {
    const newErrors = {};

    Object.keys(validationRules).forEach(fieldName => {
      try {
        if (fieldName === 'pickup_location' && !shouldShowPickupLocation()) {
          return;
        }

        const fieldValue = adData[fieldName];
        const error = validateField(fieldName, fieldValue);
        if (error) {
          newErrors[fieldName] = error;
        }
      } catch (err) {
        console.error(`Validation error for field ${fieldName}:`, err);
      }
    });

    // Additional validation for categories and subcategories
    if (categories.length === 0) {
      newErrors.category_id = "Categories are not loaded. Please refresh the page.";
    } else if (adData.category_id && !categories.some(cat => String(cat.id) === String(adData.category_id))) {
      newErrors.category_id = "Please select a valid category";
    }

    if (adData.category_id && subCategories.length === 0) {
      newErrors.sub_category_id = "Subcategories are not loaded for this category";
    } else if (adData.sub_category_id && subCategories.length > 0 && !subCategories.some(subCat => String(subCat.id) === String(adData.sub_category_id))) {
      newErrors.sub_category_id = "Please select a valid subcategory";
    }

    // Note: For edit, we don't require new images if existing ones are present
    if (!adData.thumbnail && !thumbnailPreview) {
      newErrors.thumbnail = "Thumbnail image is required";
    }

    if (selectedImageFiles.length === 0 && imagePreviewUrls.length === 0) {
      newErrors.images = "At least one additional image is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const resetSubcategories = () => {
    setSubCategories([]);
    setAdData((prev) => ({ ...prev, sub_category_id: "" }));
    setErrors((prev) => ({ ...prev, sub_category_id: "" }));
    setTouched((prev) => ({ ...prev, sub_category_id: false }));
  };

  const handleInputChange = (field, value) => {
    setAdData((prev) => ({ ...prev, [field]: value }));
    setTouched((prev) => ({ ...prev, [field]: true }));

    // Special handling for payment methods validation
    if (field === 'venmo_username' || field === 'paypal_username' || field === 'payment_method_type') {
      setTouched((prev) => ({ ...prev, payment_methods: true }));
      const error = validateField('payment_methods', value);
      setErrors((prev) => ({
        ...prev,
        payment_methods: error
      }));
    } else {
      const error = validateField(field, value);
      setErrors((prev) => ({
        ...prev,
        [field]: error
      }));
    }
  };

  const handleShippingMethodChange = (value) => {
    handleInputChange("shipping_method", value);

    if (value !== "1" && value !== 1) {
      setAdData((prev) => ({ ...prev, pickup_location: "" }));
      setErrors((prev) => ({ ...prev, pickup_location: "" }));
      setTouched((prev) => ({ ...prev, pickup_location: false }));
    }
  };

  const handleFieldBlur = (fieldName) => {
    setTouched((prev) => ({ ...prev, [fieldName]: true }));
    
    // Special handling for payment methods validation
    if (fieldName === 'venmo_username' || fieldName === 'paypal_username' || fieldName === 'payment_method_type') {
      setTouched((prev) => ({ ...prev, payment_methods: true }));
      const error = validateField('payment_methods', adData[fieldName]);
      setErrors((prev) => ({
        ...prev,
        payment_methods: error
      }));
    } else {
      const error = validateField(fieldName, adData[fieldName]);
      setErrors((prev) => ({
        ...prev,
        [fieldName]: error
      }));
    }
  };

  const hasError = (fieldName) => {
    return touched[fieldName] && errors[fieldName] && errors[fieldName] !== "";
  };

  const getErrorMessage = (fieldName) => {
    return hasError(fieldName) ? errors[fieldName] : "";
  };

  // FIXED: Load subcategories for a specific category
  const loadSubcategoriesForCategory = async (categoryId, expectedSubCategoryId = null) => {
    if (!categoryId) {
      resetSubcategories();
      return;
    }

    try {
      setSubcategoriesLoading(true);
      
      console.log("=== LOADING SUBCATEGORIES ===");
      console.log("Category ID:", categoryId);
      console.log("Expected Subcategory ID:", expectedSubCategoryId);
      
      const response = await retryApiCall(async () => {
        return await axiosInstance.post(
          `${process.env.REACT_APP_MARKET_PLACE_API_BASE_URL}v1/subcategories`,
          { category_id: parseInt(categoryId) },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${accessToken}`,
              'Accept': 'application/json'
            },
          }
        );
      });
      
      console.log("=== SUBCATEGORIES RESPONSE ===");
      console.log("Response Data:", response.data);
      
      const subcategoryData = parseApiResponse(response, 'array');
      
      console.log("=== PROCESSED SUBCATEGORIES ===");
      console.log("Subcategories count:", subcategoryData.length);
      console.log("Subcategories data:", subcategoryData);
      console.log("Available subcategory IDs:", subcategoryData.map(sub => String(sub.id)));
      
      setSubCategories(subcategoryData);
      
      // FIXED: Set subcategory after a short delay to ensure state is updated
      if (expectedSubCategoryId && subcategoryData.length > 0) {
        const foundSubCategory = subcategoryData.find(sub => String(sub.id) === String(expectedSubCategoryId));
        if (foundSubCategory) {
          console.log("✅ Found matching subcategory:", foundSubCategory);
          
          // Use setTimeout to ensure the subcategories state is fully updated before setting the value
          setTimeout(() => {
            setAdData(prev => {
              console.log("Setting subcategory ID:", String(expectedSubCategoryId));
              console.log("Previous adData:", prev);
              const newData = {
                ...prev,
                sub_category_id: String(expectedSubCategoryId)
              };
              console.log("New adData:", newData);
              return newData;
            });
          }, 100);
        } else {
          console.warn("❌ Subcategory not found in loaded data:", expectedSubCategoryId);
          console.warn("Available subcategories:", subcategoryData.map(sub => ({ id: sub.id, name: sub.name })));
          toast.warning(`Subcategory with ID ${expectedSubCategoryId} not found in current category`);
        }
      }
      
      if (subcategoryData.length === 0) {
        toast.info("No subcategories available for this category");
      } else {
        console.log(`✅ Loaded ${subcategoryData.length} subcategories successfully`);
      }
      
    } catch (err) {
      console.error("=== ERROR LOADING SUBCATEGORIES ===", err);
      
      let errorMessage = "Failed to load subcategories";
      
      if (err.response) {
        const errorData = err.response.data;
        if (errorData?.message) {
          errorMessage = errorData.message;
        } else if (err.response.status === 404) {
          errorMessage = "No subcategories found for this category";
        } else if (err.response.status === 401) {
          errorMessage = "Unauthorized - please log in again";
        } else if (err.response.status === 403) {
          errorMessage = "Access forbidden - insufficient permissions";
        } else if (err.response.status === 500) {
          errorMessage = "Server error - please try again later";
        }
      } else if (err.request) {
        errorMessage = "Network error - please check your connection";
      }
      
      toast.error(errorMessage);
      resetSubcategories();
    } finally {
      setSubcategoriesLoading(false);
    }
  };

  const handleCategoryChange = async (value) => {
    const stringValue = String(value); // Ensure consistent string type
    
    setAdData((prev) => ({ ...prev, category_id: stringValue, sub_category_id: "" }));
    resetSubcategories();

    setTouched((prev) => ({ ...prev, category_id: true }));

    const error = validateField("category_id", stringValue);
    setErrors((prev) => ({ ...prev, category_id: error }));

    if (!stringValue) return;

    // Load subcategories for the selected category
    await loadSubcategoriesForCategory(stringValue);
  };

  // Draft function - Always enabled, no validation required
  const isDraftSaveEnabled = () => {
    return true;
  };

  // Updated helper function to create a properly sized dummy image file
  const createDummyImageFile = (filename = 'draft-placeholder.png') => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      // Set minimum required dimensions (50x50 or larger)
      canvas.width = 200;
      canvas.height = 200;
      const ctx = canvas.getContext('2d');
      
      // Create a more professional looking placeholder
      // Background
      ctx.fillStyle = '#f5f5f5';
      ctx.fillRect(0, 0, 200, 200);
      
      // Border
      ctx.strokeStyle = '#e0e0e0';
      ctx.lineWidth = 2;
      ctx.strokeRect(1, 1, 198, 198);
      
      // Placeholder text
      ctx.fillStyle = '#9e9e9e';
      ctx.font = 'bold 16px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('DRAFT', 100, 90);
      ctx.fillText('IMAGE', 100, 110);
      
      // Small icon/shape
      ctx.fillStyle = '#bdbdbd';
      ctx.fillRect(85, 125, 30, 20);
      ctx.fillRect(90, 120, 20, 10);
      
      canvas.toBlob((blob) => {
        const dummyFile = new File([blob], filename, { type: 'image/png' });
        resolve(dummyFile);
      }, 'image/png', 0.8); // Added quality parameter
    });
  };

  // Enhanced function to save ad as draft - Modified for edit context
  const saveAsDraft = async () => {
    // Confirmation dialog
    const confirmed = window.confirm(
      "Are you sure you want to move this listing to draft? This will make it invisible to buyers until you publish it again."
    );
    
    if (!confirmed) {
      return;
    }

    try {
      setIsDraftSaving(true);
      dispatch(userApiLoader({ isApiLoader: true }));

      // For existing ads - use move_to_draft API
      const requestBody = {
        id: parseInt(id),
        is_draft: 1
      };

      const response = await axiosInstance.post(
        `${process.env.REACT_APP_MARKET_PLACE_API_BASE_URL}v1/ads/move_to_draft`,
        requestBody,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.data && response.data.status === 200) {
        toast.success(response.data.message || "Listing moved to draft successfully");
        setTimeout(() => {
          navigate("/myaccount/mylisting");
        }, 1500);
      } else {
        toast.error("Failed to move listing to draft");
      }
    } catch (error) {
      console.error("Save as Draft Error:", error.response || error);
      
      // Check if this is a validation error that we can ignore for drafts
      if (error.response?.status === 400 && error.response?.data?.errors) {
        const errors = error.response.data.errors;
        
        // Check if errors are only about missing fields we expect for drafts
        const expectedDraftErrors = [
          "The sub category id field is required",
          "The thumbnail field is required", 
          "The image field is required",
          "The category id field is required",
          "The name field is required",
          "The description field is required",
          "The price field is required"
        ];
        
        const isOnlyExpectedErrors = Array.isArray(errors) && 
          errors.every(error => expectedDraftErrors.some(expected => error.includes(expected)));
        
        if (isOnlyExpectedErrors) {
          // These are expected validation errors for incomplete drafts
          toast.success("Draft saved with basic information! You can complete the missing details later.");
          setTimeout(() => {
            navigate("/myaccount/mylisting");
          }, 1500);
        } else {
          // These are unexpected validation errors
          console.error("Unexpected validation errors:", errors);
          toast.error("Failed to save draft. Please try again.");
        }
      } else if (error.response?.status === 401) {
        // Authentication error
        toast.error("Session expired. Please login again.");
      } else if (error.response?.status === 500) {
        // Server error
        toast.error("Server error. Please try again later.");
      } else {
        // Other errors
        toast.error("Failed to save draft. Please try again.");
      }
    } finally {
      setIsDraftSaving(false);
      dispatch(userApiLoader({ isApiLoader: false }));
    }
  };

  // New handler for draft button that prevents validation
  const handleDraftClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Clear any existing errors before saving draft
    setErrors({});
    setTouched({});
    
    await saveAsDraft();
  };

  // Updated ad function - Modified to match the new API response structure
  const updateAd = async () => {
    const allFields = Object.keys(validationRules);
    const touchedFields = {};
    allFields.forEach(field => {
      if (field === 'pickup_location' && !shouldShowPickupLocation()) {
        return;
      }
      touchedFields[field] = true;
    });
    
    // Add payment methods to touched fields
    touchedFields.payment_methods = true;
    setTouched(touchedFields);

    const isValid = validateAllFields();

    if (!isValid) {
      toast.error("Please fix all validation errors before submitting");
      return;
    }

    dispatch(userApiLoader({ isApiLoader: true }));

    try {
      console.log("=== UPDATING AD ===");
      console.log("Ad ID:", id);

      // Create FormData for multipart/form-data request
      const formData = new FormData();

      // Add required fields
      formData.append("id", parseInt(id));
      formData.append("category_id", parseInt(adData.category_id));
      formData.append("sub_category_id", parseInt(adData.sub_category_id));
      formData.append("name", adData.name);
      formData.append("description", adData.description);
      formData.append("price", parseInt(adData.price));
      formData.append("condition", parseInt(adData.condition));
      formData.append("shipping_method", parseInt(adData.shipping_method));
      formData.append("is_draft", 0);

      // Handle payment method based on selection
      if (adData.payment_method_type === "cash") {
        formData.append("payment_method", 1); // Cash payment
      } else {
        formData.append("payment_method", 2); // Online payment
        
        // Add payment methods only for online payments
        if (adData.venmo_username && adData.venmo_username.trim() !== "") {
          const venmoUsername = adData.venmo_username.replace(/^@/, '');
          const venmoLink = `https://venmo.com/${venmoUsername}?txn=pay&amount=${parseInt(adData.price)}`;
          formData.append("venmo_id", venmoLink);
        }

        if (adData.paypal_username && adData.paypal_username.trim() !== "") {
          const paypalUsername = adData.paypal_username.replace(/^@/, '');
          const paypalLink = `paypal.me/${paypalUsername}/${parseInt(adData.price)}`;
          formData.append("paypal_id", paypalLink);
        }
      }

      // Add pickup location if required
      if (shouldShowPickupLocation() && adData.pickup_location) {
        formData.append("pickup_location", adData.pickup_location);
      }

      // Add thumbnail if new file is selected
      if (adData.thumbnail) {
        formData.append("thumbnail", adData.thumbnail);
      }

      // Add additional images if new files are selected
      if (selectedImageFiles.length > 0) {
        selectedImageFiles.forEach((file, index) => {
          formData.append(`image[${index}]`, file);
        });
      }

      console.log("=== UPDATE REQUEST DETAILS ===");
      console.log("Endpoint:", `${process.env.REACT_APP_MARKET_PLACE_API_BASE_URL}v1/ads/update`);
      console.log("Method: POST");
      console.log("Content-Type: multipart/form-data");
      
      // Log FormData contents
      for (let pair of formData.entries()) {
        console.log(pair[0] + ': ' + (pair[1] instanceof File ? `${pair[1].name} (${pair[1].size} bytes)` : pair[1]));
      }

      // Call update API
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_MARKET_PLACE_API_BASE_URL}v1/ads/update`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      console.log("=== UPDATE RESPONSE ===");
      console.log("Response Status:", response.status);
      console.log("Response Data:", response.data);

      // Handle success response based on new structure
      if (response.data.status === 200) {
        const successMessage = response.data.message || "Listing updated successfully!";
        const updatedAdData = response.data.data;
        
        // Log the updated ad data
        console.log("✅ Updated Ad Data:", updatedAdData);
        
        // Show success toast
        toast.success(successMessage, {
          position: "top-center",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });

        console.log("✅ Listing updated successfully:", successMessage);
        
        // Optional: Update local state with new data from server
        if (updatedAdData) {
          // Extract payment usernames from the response for future reference
          let venmoUsername = "";
          let paypalUsername = "";
          let paymentMethodType = "online"; // Default to online
          
          // Check if this is a cash-only ad
          if (updatedAdData.payment_methods === 1 || updatedAdData.payment_methods === "1") {
            paymentMethodType = "cash";
          } else {
            if (updatedAdData.venmo_id) {
              const venmoMatch = updatedAdData.venmo_id.match(/venmo\.com\/([^?]+)/);
              if (venmoMatch) {
                venmoUsername = venmoMatch[1];
              }
            }
            
            if (updatedAdData.paypal_id) {
              const paypalMatch = updatedAdData.paypal_id.match(/paypal\.me\/([^\/]+)/);
              if (paypalMatch) {
                paypalUsername = paypalMatch[1];
              }
            }
          }

          // Update local state with server response
          setAdData(prev => ({
            ...prev,
            name: updatedAdData.name || prev.name,
            description: updatedAdData.description || prev.description,
            price: updatedAdData.price || prev.price,
            category_id: String(updatedAdData.category_id) || prev.category_id,
            sub_category_id: String(updatedAdData.sub_category_id) || prev.sub_category_id,
            condition: updatedAdData.condition || prev.condition,
            shipping_method: updatedAdData.shipping_method || prev.shipping_method,
            payment_method_type: paymentMethodType,
            venmo_username: venmoUsername,
            paypal_username: paypalUsername,
            pickup_location: updatedAdData.pickup_location || prev.pickup_location,
          }));

          // Update image previews with server URLs
          if (updatedAdData.thumbnail) {
            setThumbnailPreview(updatedAdData.thumbnail);
          }

          if (updatedAdData.images && Array.isArray(updatedAdData.images)) {
            const imageUrls = updatedAdData.images.map(img => img.image_url).filter(Boolean);
            setImagePreviewUrls(imageUrls);
            setSelectedImageFiles([]); // Clear selected files as they're now uploaded
          }
        }
        
        // Navigate back to ad details page after a short delay
        setTimeout(() => {
          navigate(`/myaccount/mylisting/details/${id}`);
        }, 1500);
        
      } else {
        // Handle non-200 status responses
        const errorMessage = response.data.message || "Failed to update ad";
        throw new Error(errorMessage);
      }

    } catch (error) {
      console.error("=== UPDATE ERROR ===", error);
      
      let errorMessage = "Failed to update ad";
      
      // Handle different types of errors
      if (error.response) {
        // Server responded with error status
        const errorData = error.response.data;
        
        if (errorData.status === 400) {
          errorMessage = errorData.message || "Validation error occurred";
        } else if (errorData.status === 401) {
          errorMessage = "Unauthorized - please log in again";
        } else if (errorData.status === 403) {
          errorMessage = "You don't have permission to update this ad";
        } else if (errorData.status === 404) {
          errorMessage = "listing not found";
        } else if (errorData.status === 422) {
          // Handle validation errors
          if (errorData.errors && typeof errorData.errors === 'object') {
            Object.values(errorData.errors).forEach(errorArray => {
              if (Array.isArray(errorArray)) {
                errorArray.forEach(errorMsg => {
                  toast.error(errorMsg, {
                    position: "top-center",
                    autoClose: 5000
                  });
                });
              }
            });
            return; // Exit early as we've shown individual error messages
          } else {
            errorMessage = errorData.message || "Validation error occurred";
          }
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
      } else if (error.request) {
        // Network error
        errorMessage = "Network error - please check your connection";
      } else if (error.message) {
        errorMessage = error.message;
      }

      // Show error toast
      toast.error(errorMessage, {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      
      console.error("❌ Failed to update ad:", errorMessage);
      
    } finally {
      dispatch(userApiLoader({ isApiLoader: false }));
    }
  };

  // FIXED: Load categories first, then ad data
  useEffect(() => {
    const fetchCategories = async () => {
      if (!accessToken) return;

      try {
        setCategoriesLoading(true);
        dispatch(userApiLoader({ isApiLoader: true }));
        
        console.log("=== FETCHING CATEGORIES ===");
        console.log("API Endpoint:", `${process.env.REACT_APP_MARKET_PLACE_API_BASE_URL}v1/categories`);
        
        const response = await retryApiCall(async () => {
          return await axiosInstance.get(
            `${process.env.REACT_APP_MARKET_PLACE_API_BASE_URL}v1/categories`,
            { 
              headers: { 
                Authorization: `Bearer ${accessToken}`,
                'Accept': 'application/json',
                'Content-Type': 'application/json'
              } 
            }
          );
        });
        
        console.log("=== CATEGORIES RESPONSE ===");
        console.log("Response Status:", response.status);
        console.log("Response Data:", response.data);
        
        // Use the utility function to parse response
        const categoriesData = parseApiResponse(response, 'array');
        
        console.log("=== PROCESSED CATEGORIES ===");
        console.log("Categories count:", categoriesData.length);
        console.log("Categories data:", categoriesData);
        
        if (categoriesData.length > 0) {
          setCategories(categoriesData);
          console.log(`✅ Loaded ${categoriesData.length} categories successfully`);
          
          // Now load ad data if we have pending ad data
          if (pendingAdData) {
            await processAdData(pendingAdData, categoriesData);
            setPendingAdData(null);
          }
        } else {
          setCategories([]);
          toast.warning("No categories available");
        }
        
      } catch (error) {
        console.error("=== CATEGORY FETCH ERROR ===", error);
        
        let errorMessage = "Failed to load categories";
        
        if (error.response) {
          const errorData = error.response.data;
          if (errorData?.message) {
            errorMessage = errorData.message;
          } else if (error.response.status === 404) {
            errorMessage = "Categories endpoint not found";
          } else if (error.response.status === 401) {
            errorMessage = "Unauthorized - please log in again";
          } else if (error.response.status === 403) {
            errorMessage = "Access forbidden - insufficient permissions";
          } else if (error.response.status === 500) {
            errorMessage = "Server error - please try again later";
          }
        } else if (error.request) {
          errorMessage = "Network error - please check your connection";
        }
        
        toast.error(errorMessage);
        setCategories([]);
      } finally {
        setCategoriesLoading(false);
        setIsInitialDataLoaded(true);
        dispatch(userApiLoader({ isApiLoader: false }));
      }
    };

    fetchCategories();
  }, [accessToken, dispatch]);

  // Debug useEffect to track adData changes
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log("=== AD DATA CHANGED ===");
      console.log("Category ID:", adData.category_id, "(type:", typeof adData.category_id, ")");
      console.log("Subcategory ID:", adData.sub_category_id, "(type:", typeof adData.sub_category_id, ")");
      console.log("Full adData:", adData);
    }
  }, [adData.category_id, adData.sub_category_id]);

  // FIXED: Process ad data after categories are loaded - UPDATED VERSION
  const processAdData = async (adDataFromApi, categoriesData) => {
    try {
      console.log("=== PROCESSING AD DATA ===");
      console.log("Ad Data:", adDataFromApi);
      console.log("Categories Data:", categoriesData);

      // Check payment status
      const isPaid = adDataFromApi.is_paid === 1 || adDataFromApi.is_paid === "1" || adDataFromApi.is_paid === true;
      setIsPaymentCompleted(isPaid);
      
      if (isPaid) {
        toast.info("This listing has payment completed - updates may be limited");
      }

      // Extract payment usernames from URLs - updated for new field names
      let venmoUsername = "";
      let paypalUsername = "";
      let paymentMethodType = "online"; // Default to online
      
      // Check if this is a cash-only ad
      if (adDataFromApi.payment_methods === 1 || adDataFromApi.payment_methods === "1") {
        paymentMethodType = "cash";
      } else {
        // Check for both old and new field names for backward compatibility
        const venmoId = adDataFromApi.venmo_id || adDataFromApi.venmo_payment_id;
        const paypalId = adDataFromApi.paypal_id || adDataFromApi.paypal_payment_id;
        
        if (venmoId) {
          // Extract username from venmo URL: https://venmo.com/username?txn=pay&amount=...
          const venmoMatch = venmoId.match(/venmo\.com\/([^?]+)/);
          if (venmoMatch) {
            venmoUsername = venmoMatch[1];
          }
        }
        
        if (paypalId) {
          // Extract username from paypal URL: paypal.me/username/amount
          const paypalMatch = paypalId.match(/paypal\.me\/([^\/]+)/);
          if (paypalMatch) {
            paypalUsername = paypalMatch[1];
          }
        }
      }

      // FIXED: Handle missing category_id by finding it from category_name
      let categoryId = "";
      let subCategoryId = adDataFromApi.sub_category_id ? String(adDataFromApi.sub_category_id) : "";

      // If we have category_id directly, use it
      if (adDataFromApi.category_id) {
        categoryId = String(adDataFromApi.category_id);
      } 
      // If we don't have category_id but have category_name, find the ID
      else if (adDataFromApi.category_name && categoriesData.length > 0) {
        const matchingCategory = categoriesData.find(cat => 
          cat.name.toLowerCase().trim() === adDataFromApi.category_name.toLowerCase().trim()
        );
        
        if (matchingCategory) {
          categoryId = String(matchingCategory.id);
          console.log(`✅ Found category ID ${categoryId} for category name "${adDataFromApi.category_name}"`);
        } else {
          console.warn(`❌ Could not find category ID for category name: "${adDataFromApi.category_name}"`);
          console.warn("Available categories:", categoriesData.map(cat => cat.name));
        }
      }

      // FIXED: Validate that the category exists in the loaded categories
      const validCategoryId = categoryId && categoriesData.some(cat => String(cat.id) === categoryId) ? categoryId : "";
      
      console.log("=== EXTRACTED DATA ===");
      console.log("API Category Name:", adDataFromApi.category_name);
      console.log("Found Category ID:", categoryId);
      console.log("Valid Category ID:", validCategoryId);
      console.log("Subcategory ID:", subCategoryId);
      console.log("Available Categories:", categoriesData.map(cat => ({ id: cat.id, name: cat.name })));

      // FIXED: Handle shipping_option vs shipping_method mapping
      let shippingMethod = "";
      if (adDataFromApi.shipping_method) {
        shippingMethod = String(adDataFromApi.shipping_method);
      } else if (adDataFromApi.shipping_option) {
        shippingMethod = String(adDataFromApi.shipping_option);
      }

      // FIXED: Set form data in steps to ensure proper state updates
      const formData = {
        name: adDataFromApi.title || adDataFromApi.name || "",
        description: adDataFromApi.description || "",
        price: String(adDataFromApi.price || ""), // Ensure string for number inputs
        category_id: validCategoryId,
        sub_category_id: "", // Will be set after subcategories load
        condition: String(adDataFromApi.condition || ""), // Ensure string for select
        shipping_method: shippingMethod, // Use mapped shipping method
        payment_method_type: paymentMethodType,
        venmo_username: venmoUsername,
        paypal_username: paypalUsername,
        pickup_location: adDataFromApi.location || adDataFromApi.pickup_location || "",
        thumbnail: null,
        image: [],
      };

      console.log("=== FORM DATA TO SET ===");
      console.log("Form Data:", formData);

      // Set the form data
      setAdData(formData);

      // Set existing images
      if (adDataFromApi.thumbnail) {
        if (Array.isArray(adDataFromApi.thumbnail) && adDataFromApi.thumbnail.length > 0) {
          setThumbnailPreview(adDataFromApi.thumbnail[0]);
        } else if (typeof adDataFromApi.thumbnail === 'string') {
          setThumbnailPreview(adDataFromApi.thumbnail);
        }
      }
      
      if (adDataFromApi.images && Array.isArray(adDataFromApi.images)) {
        const imageUrls = adDataFromApi.images.map(img => 
          typeof img === 'string' ? img : img.image_url
        ).filter(Boolean);
        setImagePreviewUrls(imageUrls);
      }

      // FIXED: Load subcategories and set subcategory value if we have a valid category
      if (validCategoryId) {
        console.log("Loading subcategories for category:", validCategoryId);
        await loadSubcategoriesForCategory(validCategoryId, subCategoryId);
      } else if (adDataFromApi.category_name && !validCategoryId) {
        console.warn("Category name from API not found in loaded categories:", adDataFromApi.category_name);
        toast.warning(`The category "${adDataFromApi.category_name}" for this listing is no longer available`);
      }

      toast.success("Listing data loaded for editing");
    } catch (error) {
      console.error("=== ERROR PROCESSING AD DATA ===", error);
      toast.error("Error processing listing data");
    }
  };

  // FIXED: Fetch ad data - store it if categories aren't loaded yet
  useEffect(() => {
    const fetchAdData = async () => {
      if (!id || !accessToken) {
        navigate("/myaccount/mylisting");
        return;
      }

      try {
        setLoading(true);
        dispatch(userApiLoader({ isApiLoader: true }));

        console.log("=== FETCHING AD DATA FOR EDIT ===");
        console.log("Ad ID:", id);

        const response = await retryApiCall(async () => {
          return await axiosInstance.get(
            `${process.env.REACT_APP_MARKET_PLACE_API_BASE_URL}v1/ads/detail_seller/${id}`,
            {
              headers: {
                Authorization: `Bearer ${accessToken}`,
                'Accept': 'application/json',
                'Content-Type': 'application/json'
              }
            }
          );
        });

        console.log("=== AD DATA RESPONSE ===");
        console.log(response.data);

        let adDataFromApi = null;
        if (response.data.status === 200 && response.data.data) {
          adDataFromApi = response.data.data;
        } else if (response.data.success && response.data.data) {
          adDataFromApi = response.data.data;
        } else {
          adDataFromApi = response.data;
        }

        if (adDataFromApi) {
          // If categories are already loaded, process immediately
          if (categories.length > 0) {
            await processAdData(adDataFromApi, categories);
          } else {
            // Store the ad data to process later when categories are loaded
            setPendingAdData(adDataFromApi);
          }
        }
      } catch (error) {
        console.error("=== ERROR FETCHING AD FOR EDIT ===", error);
        
        if (error.response?.status === 404) {
          toast.error("listing not found");
        } else if (error.response?.status === 403) {
          toast.error("You don't have permission to edit this listing");
        } else {
          toast.error("Failed to load listing data for editing");
        }
        
        navigate("/myaccount/mylisting");
      } finally {
        setLoading(false);
        dispatch(userApiLoader({ isApiLoader: false }));
      }
    };

    if (isInitialDataLoaded) {
      fetchAdData();
    }
  }, [id, accessToken, dispatch, navigate, isInitialDataLoaded, categories]);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const renderBasicInformation = () => (
    <Card 
      elevation={1} 
      sx={{ 
        borderRadius: 2,
        backgroundColor: professionalTheme.white,
        border: `1px solid ${professionalTheme.gray[300]}`,
      }}
    >
      <CardContent sx={{ p: 4 }}>
        <Box display="flex" alignItems="center" mb={4}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              backgroundColor: professionalTheme.primary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 3,
            }}
          >
            <Description sx={{ color: professionalTheme.white, fontSize: 24 }} />
          </Box>
          <Box>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 700, 
                mb: 0.5,
                fontFamily: professionalTheme.fontFamily,
                color: professionalTheme.black
              }}
            >
              Basic Information
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                color: professionalTheme.gray[600],
                fontFamily: professionalTheme.fontFamily
              }}
            >
              Update essential details about your item
            </Typography>
          </Box>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Listing Title"
              placeholder="Enter a descriptive title for your listing"
              value={adData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              onBlur={() => handleFieldBlur("name")}
              error={hasError("name")}
              helperText={getErrorMessage("name")}
              disabled={isApiLoader}
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1,
                  fontFamily: professionalTheme.fontFamily,
                  '&:hover fieldset': {
                    borderColor: professionalTheme.primary,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: professionalTheme.primary,
                    borderWidth: 2,
                  }
                },
                '& .MuiInputLabel-root': {
                  fontFamily: professionalTheme.fontFamily,
                  '&.Mui-focused': {
                    color: professionalTheme.primary,
                  }
                },
                '& .MuiFormHelperText-root': {
                  fontFamily: professionalTheme.fontFamily,
                }
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Price ($)"
              type="number"
              placeholder="0"
              value={adData.price}
              onChange={(e) => handleInputChange("price", e.target.value)}
              onBlur={() => handleFieldBlur("price")}
              error={hasError("price")}
              helperText={getErrorMessage("price") || "Enter whole numbers only"}
              disabled={isApiLoader}
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1,
                  fontFamily: professionalTheme.fontFamily,
                  '&:hover fieldset': {
                    borderColor: professionalTheme.primary,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: professionalTheme.primary,
                    borderWidth: 2,
                  }
                },
                '& .MuiInputLabel-root': {
                  fontFamily: professionalTheme.fontFamily,
                  '&.Mui-focused': {
                    color: professionalTheme.primary,
                  }
                },
                '& .MuiFormHelperText-root': {
                  fontFamily: professionalTheme.fontFamily,
                }
              }}
              InputProps={{
                startAdornment: <AttachMoney sx={{ mr: 1, color: professionalTheme.gray[600] }} />
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              multiline
              rows={4}
              placeholder="Provide detailed information about your item..."
              value={adData.description}
              onChange={(e) => handleInputChange("description", e.target.value)}
              onBlur={() => handleFieldBlur("description")}
              error={hasError("description")}
              helperText={getErrorMessage("description") || "Be descriptive to attract more buyers"}
              disabled={isApiLoader}
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1,
                  fontFamily: professionalTheme.fontFamily,
                  '&:hover fieldset': {
                    borderColor: professionalTheme.primary,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: professionalTheme.primary,
                    borderWidth: 2,
                  }
                },
                '& .MuiInputLabel-root': {
                  fontFamily: professionalTheme.fontFamily,
                  '&.Mui-focused': {
                    color: professionalTheme.primary,
                  }
                },
                '& .MuiFormHelperText-root': {
                  fontFamily: professionalTheme.fontFamily,
                }
              }}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const renderCategoryAndDetails = () => (
    <Card 
      elevation={1} 
      sx={{ 
        borderRadius: 2,
        backgroundColor: professionalTheme.white,
        border: `1px solid ${professionalTheme.gray[300]}`,
      }}
    >
      <CardContent sx={{ p: 4 }}>
        <Box display="flex" alignItems="center" mb={4}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              backgroundColor: professionalTheme.primary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 3,
            }}
          >
            <Category sx={{ color: professionalTheme.white, fontSize: 24 }} />
          </Box>
          <Box>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 700, 
                mb: 0.5,
                fontFamily: professionalTheme.fontFamily,
                color: professionalTheme.black
              }}
            >
              Category & Details
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                color: professionalTheme.gray[600],
                fontFamily: professionalTheme.fontFamily
              }}
            >
              {isPaymentCompleted ? 
                "Item details and payment information (Payment completed ✅)" : 
                "Update your item's category and selling details"
              }
            </Typography>
          </Box>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 700, 
                mb: 2,
                fontFamily: professionalTheme.fontFamily,
                color: professionalTheme.black
              }}
            >
              Product Categories
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: professionalTheme.gray[600],
                fontFamily: professionalTheme.fontFamily,
                mb: 3
              }}
            >
              Select the appropriate category and subcategory for your item. Subcategories will load after selecting a category.
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControl 
              fullWidth 
              error={hasError("category_id")} 
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1,
                  fontFamily: professionalTheme.fontFamily,
                  '&:hover fieldset': {
                    borderColor: professionalTheme.primary,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: professionalTheme.primary,
                    borderWidth: 2,
                  }
                },
                '& .MuiInputLabel-root': {
                  fontFamily: professionalTheme.fontFamily,
                  '&.Mui-focused': {
                    color: professionalTheme.primary,
                  }
                }
              }}
            >
              <InputLabel>Category</InputLabel>
              <Select
                value={adData.category_id || ""} // Ensure empty string fallback
                onChange={(e) => handleCategoryChange(e.target.value)}
                onBlur={() => handleFieldBlur("category_id")}
                label="Category"
                disabled={isApiLoader || categoriesLoading || categories.length === 0}
                sx={{ fontFamily: professionalTheme.fontFamily }}
                displayEmpty
              >
                <MenuItem value="" disabled sx={{ fontFamily: professionalTheme.fontFamily }}>
                  <em>
                    {categoriesLoading 
                      ? "Loading categories..." 
                      : categories.length === 0 
                        ? "No categories available" 
                        : "Select a category"
                    }
                  </em>
                </MenuItem>
                {categories.map((category) => (
                  <MenuItem 
                    key={category.id} 
                    value={String(category.id)}
                    sx={{ fontFamily: professionalTheme.fontFamily }}
                  >
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
              {hasError("category_id") && (
                <FormHelperText sx={{ fontFamily: professionalTheme.fontFamily }}>
                  {getErrorMessage("category_id")}
                </FormHelperText>
              )}
              {categoriesLoading && (
                <FormHelperText sx={{ fontFamily: professionalTheme.fontFamily, color: 'info.main' }}>
                  Loading categories...
                </FormHelperText>
              )}
              {!categoriesLoading && categories.length === 0 && !isApiLoader && (
                <FormHelperText sx={{ fontFamily: professionalTheme.fontFamily, color: 'warning.main' }}>
                  No categories available
                </FormHelperText>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl 
              fullWidth 
              error={hasError("sub_category_id")} 
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1,
                  fontFamily: professionalTheme.fontFamily,
                  '&:hover fieldset': {
                    borderColor: professionalTheme.primary,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: professionalTheme.primary,
                    borderWidth: 2,
                  }
                },
                '& .MuiInputLabel-root': {
                  fontFamily: professionalTheme.fontFamily,
                  '&.Mui-focused': {
                    color: professionalTheme.primary,
                  }
                }
              }}
            >
              <InputLabel>Subcategory</InputLabel>
              <Select
                value={adData.sub_category_id || ""} // Ensure empty string fallback
                onChange={(e) => handleInputChange("sub_category_id", e.target.value)}
                onBlur={() => handleFieldBlur("sub_category_id")}
                label="Subcategory"
                disabled={!adData.category_id || isApiLoader || subcategoriesLoading}
                sx={{ fontFamily: professionalTheme.fontFamily }}
                displayEmpty
              >
                <MenuItem value="" disabled sx={{ fontFamily: professionalTheme.fontFamily }}>
                  <em>
                    {!adData.category_id 
                      ? "Select category first" 
                      : subcategoriesLoading 
                        ? "Loading subcategories..." 
                        : subCategories.length === 0 
                          ? "No subcategories available" 
                          : "Select a subcategory"
                    }
                  </em>
                </MenuItem>
                {subCategories.map((subCategory) => (
                  <MenuItem 
                    key={subCategory.id} 
                    value={String(subCategory.id)}
                    sx={{ fontFamily: professionalTheme.fontFamily }}
                  >
                    {subCategory.name}
                  </MenuItem>
                ))}
              </Select>
              {hasError("sub_category_id") && (
                <FormHelperText sx={{ fontFamily: professionalTheme.fontFamily }}>
                  {getErrorMessage("sub_category_id")}
                </FormHelperText>
              )}
              {subcategoriesLoading && (
                <FormHelperText sx={{ fontFamily: professionalTheme.fontFamily, color: 'info.main' }}>
                  Loading subcategories...
                </FormHelperText>
              )}
              {!subcategoriesLoading && adData.category_id && subCategories.length === 0 && !isApiLoader && (
                <FormHelperText sx={{ fontFamily: professionalTheme.fontFamily, color: 'warning.main' }}>
                  No subcategories available for this category
                </FormHelperText>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl 
              fullWidth 
              error={hasError("condition")} 
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1,
                  fontFamily: professionalTheme.fontFamily,
                  '&:hover fieldset': {
                    borderColor: professionalTheme.primary,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: professionalTheme.primary,
                    borderWidth: 2,
                  }
                },
                '& .MuiInputLabel-root': {
                  fontFamily: professionalTheme.fontFamily,
                  '&.Mui-focused': {
                    color: professionalTheme.primary,
                  }
                }
              }}
            >
              <InputLabel>Item Condition</InputLabel>
              <Select
                value={adData.condition}
                onChange={(e) => handleInputChange("condition", e.target.value)}
                onBlur={() => handleFieldBlur("condition")}
                label="Item Condition"
                disabled={isApiLoader}
                sx={{ fontFamily: professionalTheme.fontFamily }}
              >
                <MenuItem value={1} sx={{ fontFamily: professionalTheme.fontFamily }}>
                  <Box display="flex" alignItems="center">
                    <Chip 
                      label="New" 
                      color="success" 
                      size="small" 
                      sx={{ 
                        mr: 2, 
                        fontFamily: professionalTheme.fontFamily,
                        fontWeight: 500
                      }} 
                    />
                    New
                  </Box>
                </MenuItem>
                <MenuItem value={2} sx={{ fontFamily: professionalTheme.fontFamily }}>
                  <Box display="flex" alignItems="center">
                    <Chip 
                      label="Like New" 
                      color="info" 
                      size="small" 
                      sx={{ 
                        mr: 2, 
                        fontFamily: professionalTheme.fontFamily,
                        fontWeight: 500
                      }} 
                    />
                    Like New
                  </Box>
                </MenuItem>
                <MenuItem value={3} sx={{ fontFamily: professionalTheme.fontFamily }}>
                  <Box display="flex" alignItems="center">
                    <Chip 
                      label="Good" 
                      color="primary" 
                      size="small" 
                      sx={{ 
                        mr: 2, 
                        fontFamily: professionalTheme.fontFamily,
                        fontWeight: 500
                      }} 
                    />
                    Good
                  </Box>
                </MenuItem>
                <MenuItem value={4} sx={{ fontFamily: professionalTheme.fontFamily }}>
                  <Box display="flex" alignItems="center">
                    <Chip 
                      label="Fair" 
                      color="warning" 
                      size="small" 
                      sx={{ 
                        mr: 2, 
                        fontFamily: professionalTheme.fontFamily,
                        fontWeight: 500
                      }} 
                    />
                    Fair
                  </Box>
                </MenuItem>
                <MenuItem value={5} sx={{ fontFamily: professionalTheme.fontFamily }}>
                  <Box display="flex" alignItems="center">
                    <Chip 
                      label="Poor" 
                      color="error" 
                      size="small" 
                      sx={{ 
                        mr: 2, 
                        fontFamily: professionalTheme.fontFamily,
                        fontWeight: 500
                      }} 
                    />
                    Poor
                  </Box>
                </MenuItem>
              </Select>
              {hasError("condition") && (
                <FormHelperText sx={{ fontFamily: professionalTheme.fontFamily }}>
                  {getErrorMessage("condition")}
                </FormHelperText>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl 
              fullWidth 
              error={hasError("shipping_method")} 
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1,
                  fontFamily: professionalTheme.fontFamily,
                  '&:hover fieldset': {
                    borderColor: professionalTheme.primary,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: professionalTheme.primary,
                    borderWidth: 2,
                  }
                },
                '& .MuiInputLabel-root': {
                  fontFamily: professionalTheme.fontFamily,
                  '&.Mui-focused': {
                    color: professionalTheme.primary,
                  }
                }
              }}
            >
              <InputLabel>Shipping Method</InputLabel>
              <Select
                value={adData.shipping_method}
                onChange={(e) => handleShippingMethodChange(e.target.value)}
                onBlur={() => handleFieldBlur("shipping_method")}
                label="Shipping Method"
                disabled={isApiLoader}
                sx={{ fontFamily: professionalTheme.fontFamily }}
              >
                <MenuItem value={1} sx={{ fontFamily: professionalTheme.fontFamily }}>Pickup Only</MenuItem>
                <MenuItem value={2} sx={{ fontFamily: professionalTheme.fontFamily }}>Delivery Only</MenuItem>
              </Select>
              {hasError("shipping_method") && (
                <FormHelperText sx={{ fontFamily: professionalTheme.fontFamily }}>
                  {getErrorMessage("shipping_method")}
                </FormHelperText>
              )}
            </FormControl>
          </Grid>

          {shouldShowPickupLocation() && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Pickup Location"
                placeholder="Enter pickup location address"
                value={adData.pickup_location}
                onChange={(e) => handleInputChange("pickup_location", e.target.value)}
                onBlur={() => handleFieldBlur("pickup_location")}
                error={hasError("pickup_location")}
                helperText={getErrorMessage("pickup_location") || `${adData.pickup_location.length}/255 characters`}
                disabled={isApiLoader}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1,
                    fontFamily: professionalTheme.fontFamily,
                    backgroundColor: professionalTheme.gray[50],
                    '&:hover fieldset': {
                      borderColor: professionalTheme.primary,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: professionalTheme.primary,
                      borderWidth: 2,
                    }
                  },
                  '& .MuiInputLabel-root': {
                    fontFamily: professionalTheme.fontFamily,
                    '&.Mui-focused': {
                      color: professionalTheme.primary,
                    }
                  },
                  '& .MuiFormHelperText-root': {
                    fontFamily: professionalTheme.fontFamily,
                  }
                }}
                InputProps={{
                  startAdornment: <LocationOn sx={{ mr: 1, color: professionalTheme.gray[600] }} />
                }}
              />
            </Grid>
          )}

          {/* Payment Methods Section */}
          <Grid item xs={12}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 700, 
                mb: 2,
                fontFamily: professionalTheme.fontFamily,
                color: professionalTheme.black
              }}
            >
              Payment Methods
              {isPaymentCompleted && (
                <Chip 
                  label="PAID" 
                  color="success" 
                  size="small" 
                  sx={{ ml: 2, fontFamily: professionalTheme.fontFamily }}
                />
              )}
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: professionalTheme.gray[600],
                fontFamily: professionalTheme.fontFamily,
                mb: 3
              }}
            >
              Choose how you want to receive payment for this item.
            </Typography>
          </Grid>

          {/* Payment Method Type Selection */}
          <Grid item xs={12}>
            <FormControl 
              fullWidth
              error={hasError("payment_method_type")}
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1,
                  fontFamily: professionalTheme.fontFamily,
                  '&:hover fieldset': {
                    borderColor: professionalTheme.primary,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: professionalTheme.primary,
                    borderWidth: 2,
                  }
                },
                '& .MuiInputLabel-root': {
                  fontFamily: professionalTheme.fontFamily,
                  '&.Mui-focused': {
                    color: professionalTheme.primary,
                  }
                }
              }}
            >
              <InputLabel>Payment Method</InputLabel>
              <Select
                value={adData.payment_method_type}
                onChange={(e) => {
                  const value = e.target.value;
                  handleInputChange("payment_method_type", value);
                  // Clear online payment fields when switching to cash
                  if (value === "cash") {
                    setAdData(prev => ({
                      ...prev,
                      venmo_username: "",
                      paypal_username: ""
                    }));
                    setErrors(prev => ({
                      ...prev,
                      payment_methods: ""
                    }));
                  }
                }}
                onBlur={() => handleFieldBlur("payment_method_type")}
                label="Payment Method"
                disabled={isApiLoader}
                sx={{ fontFamily: professionalTheme.fontFamily }}
              >
                <MenuItem value="online" sx={{ fontFamily: professionalTheme.fontFamily }}>
                  <Box display="flex" alignItems="center">
                    <Payment sx={{ mr: 2, color: professionalTheme.gray[600] }} />
                    Online Payment (Venmo/PayPal)
                  </Box>
                </MenuItem>
                <MenuItem value="cash" sx={{ fontFamily: professionalTheme.fontFamily }}>
                  <Box display="flex" alignItems="center">
                    <AttachMoney sx={{ mr: 2, color: professionalTheme.gray[600] }} />
                    Cash Only
                  </Box>
                </MenuItem>
              </Select>
              {hasError("payment_method_type") && (
                <FormHelperText sx={{ fontFamily: professionalTheme.fontFamily }}>
                  {getErrorMessage("payment_method_type")}
                </FormHelperText>
              )}
            </FormControl>
          </Grid>

          {/* Cash Only Information */}
          {adData.payment_method_type === "cash" && (
            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 2,
                  backgroundColor: '#fff3e0',
                  border: '1px solid #ffb74d'
                }}
              >
                <Box display="flex" alignItems="center">
                  <AttachMoney sx={{ color: '#f57c00', mr: 2, fontSize: 28 }} />
                  <Box>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 700, 
                        color: '#ef6c00',
                        fontFamily: professionalTheme.fontFamily,
                        mb: 0.5
                      }}
                    >
                      Cash Only Payment
                    </Typography>
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        color: '#f57c00',
                        fontFamily: professionalTheme.fontFamily
                      }}
                    >
                      You have selected cash only payment. Buyers will know to bring cash for pickup or arrange cash payment for delivery.
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          )}

          {/* Online Payment Fields - Only show when online payment is selected */}
          {adData.payment_method_type === "online" && (
            <>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Venmo Username"
                  placeholder="Enter your Venmo username (e.g., john-doe)"
                  value={adData.venmo_username}
                  onChange={(e) => handleInputChange("venmo_username", e.target.value)}
                  onBlur={() => handleFieldBlur("venmo_username")}
                  error={hasError("payment_methods") && !adData.venmo_username && !adData.paypal_username}
                  helperText={
                    isPaymentCompleted && adData.venmo_username ? 
                      "✅ Venmo payment completed" : 
                      "Optional - Just your username, we'll create the payment link"
                  }
                  disabled={isApiLoader}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1,
                      fontFamily: professionalTheme.fontFamily,
                      backgroundColor: isPaymentCompleted && adData.venmo_username ? '#e8f5e8' : professionalTheme.gray[50],
                      '&:hover fieldset': {
                        borderColor: professionalTheme.primary,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: professionalTheme.primary,
                        borderWidth: 2,
                      }
                    },
                    '& .MuiInputLabel-root': {
                      fontFamily: professionalTheme.fontFamily,
                      '&.Mui-focused': {
                        color: professionalTheme.primary,
                      }
                    },
                    '& .MuiFormHelperText-root': {
                      fontFamily: professionalTheme.fontFamily,
                      color: isPaymentCompleted && adData.venmo_username ? '#4caf50' : 'inherit'
                    }
                  }}
                  InputProps={{
                    startAdornment: <Payment sx={{ mr: 1, color: professionalTheme.gray[600] }} />,
                    endAdornment: isPaymentCompleted && adData.venmo_username && (
                      <CheckCircle sx={{ color: '#4caf50', ml: 1 }} />
                    )
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="PayPal Username"
                  placeholder="Enter your PayPal username (e.g., john-doe)"
                  value={adData.paypal_username}
                  onChange={(e) => handleInputChange("paypal_username", e.target.value)}
                  onBlur={() => handleFieldBlur("paypal_username")}
                  error={hasError("payment_methods") && !adData.venmo_username && !adData.paypal_username}
                  helperText={
                    isPaymentCompleted && adData.paypal_username ? 
                      "✅ PayPal payment completed" : 
                      "Optional - Just your username, we'll create the PayPal.me link"
                  }
                  disabled={isApiLoader}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1,
                      fontFamily: professionalTheme.fontFamily,
                      backgroundColor: isPaymentCompleted && adData.paypal_username ? '#e8f5e8' : professionalTheme.gray[50],
                      '&:hover fieldset': {
                        borderColor: professionalTheme.primary,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: professionalTheme.primary,
                        borderWidth: 2,
                      }
                    },
                    '& .MuiInputLabel-root': {
                      fontFamily: professionalTheme.fontFamily,
                      '&.Mui-focused': {
                        color: professionalTheme.primary,
                      }
                    },
                    '& .MuiFormHelperText-root': {
                      fontFamily: professionalTheme.fontFamily,
                      color: isPaymentCompleted && adData.paypal_username ? '#4caf50' : 'inherit'
                    }
                  }}
                  InputProps={{
                    startAdornment: <AccountBalance sx={{ mr: 1, color: professionalTheme.gray[600] }} />,
                    endAdornment: isPaymentCompleted && adData.paypal_username && (
                      <CheckCircle sx={{ color: '#4caf50', ml: 1 }} />
                    )
                  }}
                />
              </Grid>

              {/* Show error message if no payment method is provided for online payments */}
              {hasError("payment_methods") && (
                <Grid item xs={12}>
                  <Typography 
                    color="error" 
                    variant="body2" 
                    sx={{ 
                      fontFamily: professionalTheme.fontFamily,
                      backgroundColor: '#ffebee',
                      p: 2,
                      borderRadius: 1,
                      border: '1px solid #f44336'
                    }}
                  >
                    {getErrorMessage("payment_methods")}
                  </Typography>
                </Grid>
              )}
            </>
          )}
        </Grid>
      </CardContent>
    </Card>
  );

  const renderImagesAndMedia = () => (
    <Card 
      elevation={1} 
      sx={{ 
        borderRadius: 2,
        backgroundColor: professionalTheme.white,
        border: `1px solid ${professionalTheme.gray[300]}`,
      }}
    >
      <CardContent sx={{ p: 4 }}>
        <Box display="flex" alignItems="center" mb={4}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              backgroundColor: professionalTheme.primary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 3,
            }}
          >
            <ImageIcon sx={{ color: professionalTheme.white, fontSize: 24 }} />
          </Box>
          <Box>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 700, 
                mb: 0.5,
                fontFamily: professionalTheme.fontFamily,
                color: professionalTheme.black
              }}
            >
              Images & Media
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                color: professionalTheme.gray[600],
                fontFamily: professionalTheme.fontFamily
              }}
            >
              Update images to showcase your item (existing images will be kept if not changed)
            </Typography>
          </Box>
        </Box>

        <Grid container spacing={4}>
          {/* Thumbnail Upload */}
          <Grid item xs={12}>
            <Typography 
              variant="subtitle1" 
              gutterBottom 
              sx={{ 
                fontWeight: 600, 
                mb: 2,
                fontFamily: professionalTheme.fontFamily,
                color: professionalTheme.black
              }}
            >
              Thumbnail (Main Image) *
            </Typography>
            <Paper
              elevation={0}
              sx={{
                border: `2px dashed ${hasError("thumbnail") ? "#f44336" : professionalTheme.gray[400]}`,
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                backgroundColor: professionalTheme.gray[50],
                '&:hover': {
                  borderColor: professionalTheme.primary,
                  backgroundColor: professionalTheme.gray[100],
                }
              }}
              onClick={() => !isApiLoader && document.getElementById("thumbnail-upload").click()}
            >
              {thumbnailPreview ? (
                <Box position="relative" display="inline-block">
                  <img
                    src={thumbnailPreview}
                    alt="Thumbnail preview"
                    style={{ 
                      maxWidth: 200, 
                      maxHeight: 200, 
                      borderRadius: 8, 
                      objectFit: 'cover',
                      border: `1px solid ${professionalTheme.gray[300]}`
                    }}
                  />
                  <IconButton
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: -8,
                      right: -8,
                      backgroundColor: professionalTheme.primary,
                      color: professionalTheme.white,
                      '&:hover': { 
                        backgroundColor: professionalTheme.gray[800],
                      }
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setAdData((prev) => ({ ...prev, thumbnail: null }));
                      setThumbnailPreview(null);
                    }}
                    disabled={isApiLoader}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </Box>
              ) : (
                <Box>
                  <CloudUpload sx={{ fontSize: 48, color: professionalTheme.gray[600], mb: 2 }} />
                  <Typography 
                    variant="subtitle1" 
                    sx={{ 
                      fontWeight: 600, 
                      mb: 1,
                      fontFamily: professionalTheme.fontFamily,
                      color: professionalTheme.black
                    }}
                  >
                    Upload New Thumbnail
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: professionalTheme.gray[600],
                      fontFamily: professionalTheme.fontFamily,
                      mb: 2
                    }}
                  >
                    Click to select a new main image (optional - existing will be kept)
                  </Typography>
                  <Chip 
                    label="Max 2MB" 
                    size="small"
                    sx={{ 
                      backgroundColor: professionalTheme.gray[200],
                      color: professionalTheme.gray[700],
                      fontFamily: professionalTheme.fontFamily,
                      fontWeight: 500
                    }}
                  />
                </Box>
              )}
            </Paper>
            
            <input
              accept="image/*"
              style={{ display: "none" }}
              id="thumbnail-upload"
              type="file"
              disabled={isApiLoader}
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  if (file.size > 2 * 1024 * 1024) {
                    setErrors((prev) => ({ ...prev, thumbnail: "File size must be less than 2MB" }));
                    return;
                  }
                  setAdData((prev) => ({ ...prev, thumbnail: file }));
                  setThumbnailPreview(URL.createObjectURL(file));
                  setErrors((prev) => ({ ...prev, thumbnail: "" }));
                  setTouched((prev) => ({ ...prev, thumbnail: true }));
                }
              }}
            />

            {hasError("thumbnail") && (
              <Typography 
                color="error" 
                variant="body2" 
                sx={{ 
                  mt: 1, 
                  fontFamily: professionalTheme.fontFamily
                }}
              >
                {getErrorMessage("thumbnail")}
              </Typography>
            )}
          </Grid>

          {/* Additional Images */}
          <Grid item xs={12}>
            <Typography 
              variant="subtitle1" 
              gutterBottom 
              sx={{ 
                fontWeight: 600, 
                mb: 2,
                fontFamily: professionalTheme.fontFamily,
                color: professionalTheme.black
              }}
            >
              Additional Images (Max 5) *
            </Typography>
            <Paper
              elevation={0}
              sx={{
                border: `2px dashed ${hasError("images") ? "#f44336" : professionalTheme.gray[400]}`,
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                backgroundColor: professionalTheme.gray[50],
                '&:hover': {
                  borderColor: professionalTheme.primary,
                  backgroundColor: professionalTheme.gray[100],
                }
              }}
              onClick={() => !isApiLoader && document.getElementById("images-upload").click()}
            >
              <CloudUpload sx={{ fontSize: 48, color: professionalTheme.gray[600], mb: 2 }} />
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  fontWeight: 600, 
                  mb: 1,
                  fontFamily: professionalTheme.fontFamily,
                  color: professionalTheme.black
                }}
              >
                Upload New Additional Images
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: professionalTheme.gray[600],
                  fontFamily: professionalTheme.fontFamily,
                  mb: 2
                }}
              >
                Click to select new images (optional - existing images will be kept)
              </Typography>
              <Chip 
                label="Max 5MB each" 
                size="small"
                sx={{ 
                  backgroundColor: professionalTheme.gray[200],
                  color: professionalTheme.gray[700],
                  fontFamily: professionalTheme.fontFamily,
                  fontWeight: 500
                }}
              />
            </Paper>

            <input
              accept="image/*"
              style={{ display: "none" }}
              id="images-upload"
              type="file"
              multiple
              disabled={isApiLoader}
              onChange={(e) => {
                const files = Array.from(e.target.files);

                if (files.length > 5) {
                  setErrors((prev) => ({ ...prev, additionalImages: "Maximum 5 additional images allowed" }));
                  return;
                }

                const oversizedFiles = files.filter(file => file.size > 5 * 1024 * 1024);
                if (oversizedFiles.length > 0) {
                  setErrors((prev) => ({ ...prev, additionalImages: "Each file size must be less than 5MB" }));
                  return;
                }

                if (files.length > 0) {
                  setSelectedImageFiles(files);
                  setImagePreviewUrls(files.map((file) => URL.createObjectURL(file)));
                  setErrors((prev) => ({ ...prev, additionalImages: "", images: "" }));
                }
              }}
            />

            {imagePreviewUrls.length > 0 && (
              <Box mt={3}>
                <Typography 
                  variant="subtitle1" 
                  gutterBottom 
                  sx={{ 
                    fontWeight: 600,
                    fontFamily: professionalTheme.fontFamily,
                    color: professionalTheme.black
                  }}
                >
                  {selectedImageFiles.length > 0 ? 
                    `New Images (${imagePreviewUrls.length}/5)` : 
                    `Current Images (${imagePreviewUrls.length})`
                  }
                </Typography>
                <Grid container spacing={2}>
                  {imagePreviewUrls.map((url, index) => (
                    <Grid item xs={6} sm={4} md={3} key={index}>
                      <Paper 
                        elevation={1} 
                        sx={{ 
                          position: 'relative', 
                          p: 1,
                          borderRadius: 2,
                          border: `1px solid ${professionalTheme.gray[300]}`
                        }}
                      >
                        <img
                          src={url}
                          alt={`Preview ${index + 1}`}
                          style={{ 
                            width: '100%', 
                            height: 120, 
                            objectFit: 'cover', 
                            borderRadius: 4
                          }}
                        />
                        {selectedImageFiles.length > 0 && (
                          <IconButton
                            size="small"
                            sx={{
                              position: 'absolute',
                              top: -8,
                              right: -8,
                              backgroundColor: professionalTheme.primary,
                              color: professionalTheme.white,
                              '&:hover': { 
                                backgroundColor: professionalTheme.gray[800],
                              }
                            }}
                            onClick={() => {
                              const updatedFiles = [...selectedImageFiles];
                              const updatedUrls = [...imagePreviewUrls];
                              updatedFiles.splice(index, 1);
                              updatedUrls.splice(index, 1);
                              setSelectedImageFiles(updatedFiles);
                              setImagePreviewUrls(updatedUrls);
                            }}
                            disabled={isApiLoader}
                          >
                            <Close fontSize="small" />
                          </IconButton>
                        )}
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {hasError("images") && (
              <Typography 
                color="error" 
                variant="body2" 
                sx={{ 
                  mt: 1,
                  fontFamily: professionalTheme.fontFamily
                }}
              >
                {getErrorMessage("images")}
              </Typography>
            )}
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return renderBasicInformation();
      case 1:
        return renderCategoryAndDetails();
      case 2:
        return renderImagesAndMedia();
      default:
        return null;
    }
  };

  const isFormValid = () => {
    const hasRequiredFields = adData.name && adData.description && adData.price && 
                             adData.category_id && adData.sub_category_id && 
                             adData.condition && adData.shipping_method &&
                             adData.payment_method_type;
    
    const hasPaymentMethod = (
      adData.payment_method_type === "cash" ||
      (adData.venmo_username && adData.venmo_username.trim() !== "") ||
      (adData.paypal_username && adData.paypal_username.trim() !== "")
    );
    
    const hasPickupLocationIfNeeded = !shouldShowPickupLocation() || adData.pickup_location;
    const hasImages = thumbnailPreview || (selectedImageFiles.length > 0 || imagePreviewUrls.length > 0);
    const hasNoErrors = Object.keys(errors).every(key => !errors[key] || errors[key] === "");
    
    // Additional validation for categories and subcategories
    const hasValidCategory = categories.length > 0 && adData.category_id && categories.some(cat => String(cat.id) === String(adData.category_id));
    const hasValidSubcategory = subCategories.length > 0 && adData.sub_category_id && subCategories.some(subCat => String(subCat.id) === String(adData.sub_category_id));
    
    return hasRequiredFields && hasPaymentMethod && hasPickupLocationIfNeeded && hasImages && 
           hasNoErrors && hasValidCategory && hasValidSubcategory;
  };

  if (loading) {
    return (
      <Box 
        sx={{ 
          minHeight: '100vh', 
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: professionalTheme.fontFamily
        }}
      >
        <Card 
          elevation={4}
          sx={{ 
            p: 6, 
            borderRadius: 2,
            textAlign: 'center',
            maxWidth: 400
          }}
        >
          <img
            src={loadingGif}
            alt="Loading..."
            style={{ width: 48, height: 48 }}
          />
          <Typography 
            variant="h6" 
            sx={{ 
              mt: 3, 
              fontWeight: 600,
              color: professionalTheme.black,
              fontFamily: professionalTheme.fontFamily
            }}
          >
            Loading listing data...
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              mt: 1, 
              color: professionalTheme.gray[600],
              fontFamily: professionalTheme.fontFamily
            }}
          >
            Please wait while we fetch your listing details
          </Typography>
        </Card>
      </Box>
    );
  }

  return (
    <Box 
      sx={{ 
        minHeight: '100vh', 
        py: 4,
        fontFamily: professionalTheme.fontFamily
      }}
    >
      <LoadingOverlay 
        isLoading={isApiLoader} 
        isPaymentCompleted={isPaymentCompleted}
        isDraftSaving={isDraftSaving}
      />
      
      <Box maxWidth="lg" mx="auto" px={3}>
        {/* Header */}
        <Paper 
          elevation={2} 
          sx={{ 
            p: 4, 
            mb: 4, 
            borderRadius: 2,
            backgroundColor: professionalTheme.white,
            border: `1px solid ${professionalTheme.gray[300]}`
          }}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center">
              <IconButton 
                onClick={() => navigate(`/myaccount/mylisting/details/${id}`)}
                sx={{ 
                  mr: 3,
                  backgroundColor: professionalTheme.gray[100],
                  '&:hover': {
                    backgroundColor: professionalTheme.gray[200],
                  }
                }}
                disabled={isApiLoader}
              >
                <ArrowBack sx={{ color: professionalTheme.primary }} />
              </IconButton>
              <Box>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    fontWeight: 700, 
                    mb: 1,
                    fontFamily: professionalTheme.fontFamily,
                    color: professionalTheme.black
                  }}
                >
                  Edit Listing
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    color: professionalTheme.gray[600],
                    fontFamily: professionalTheme.fontFamily
                  }}
                >
                  Update your existing listing with new details
                </Typography>
              </Box>
            </Box>
            
            {/* Payment Status Indicator */}
            {isPaymentCompleted && (
              <Chip
                icon={<CheckCircle sx={{ fontSize: 18 }} />}
                label="Payment Completed"
                sx={{
                  backgroundColor: '#e8f5e8',
                  color: '#4caf50',
                  fontFamily: professionalTheme.fontFamily,
                  fontWeight: 600,
                  fontSize: '14px',
                  '& .MuiChip-icon': {
                    color: '#4caf50'
                  }
                }}
              />
            )}
          </Box>
        </Paper>

        {/* Payment Status Alert */}
        {isPaymentCompleted && (
          <Paper 
            elevation={1} 
            sx={{ 
              p: 3, 
              mb: 4, 
              borderRadius: 2,
              backgroundColor: '#e8f5e8',
              border: '1px solid #4caf50'
            }}
          >
            <Box display="flex" alignItems="center">
              <CheckCircle sx={{ color: '#4caf50', mr: 2, fontSize: 28 }} />
              <Box>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 700, 
                    color: '#2e7d32',
                    fontFamily: professionalTheme.fontFamily,
                    mb: 0.5
                  }}
                >
                  Payment Completed ✅
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    color: '#388e3c',
                    fontFamily: professionalTheme.fontFamily
                  }}
                >
                  The buyer has successfully completed the payment for this item. You can still update details but proceed with caution.
                </Typography>
              </Box>
            </Box>
          </Paper>
        )}

        {/* Stepper */}
        <Paper 
          elevation={2} 
          sx={{ 
            p: 4, 
            mb: 4, 
            borderRadius: 2,
            backgroundColor: professionalTheme.white,
            border: `1px solid ${professionalTheme.gray[300]}`
          }}
        >
          <ProfessionalStepper activeStep={activeStep} steps={steps} />
        </Paper>

        {/* Form Content */}
        <Box mb={4}>
          {renderStepContent(activeStep)}
        </Box>

        {/* Navigation Buttons */}
        <Paper 
          elevation={2} 
          sx={{ 
            p: 4, 
            borderRadius: 2,
            backgroundColor: professionalTheme.white,
            border: `1px solid ${professionalTheme.gray[300]}`
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Button
              onClick={handleBack}
              disabled={activeStep === 0 || isApiLoader}
              startIcon={<ArrowBack />}
              size="large"
              sx={{
                borderRadius: 1,
                px: 4,
                py: 1.5,
                fontWeight: 600,
                textTransform: 'none',
                fontSize: '16px',
                fontFamily: professionalTheme.fontFamily,
                color: professionalTheme.gray[700],
                border: `1px solid ${professionalTheme.gray[400]}`,
                '&:hover': {
                  backgroundColor: professionalTheme.gray[100],
                  borderColor: professionalTheme.primary,
                }
              }}
            >
              Back
            </Button>

            <Box display="flex" gap={3}>
              {/* Save as Draft Button - Always available, no validation */}
              <Button
                variant="outlined"
                onClick={handleDraftClick}
                disabled={isApiLoader || isDraftSaving}
                startIcon={<Save />}
                size="large"
                title="Move this listing to draft - it will become invisible to buyers until published again"
                sx={{
                  borderRadius: 1,
                 padding:'2px 6px',
                  fontWeight: 600,
                  textTransform: 'none',
                  width:'180px',
                  height:'55px',
                  fontSize: '16px',
                  fontFamily: professionalTheme.fontFamily,
                  color: professionalTheme.gray[700],
                  borderColor: professionalTheme.gray[400],
                  '&:hover': {
                    backgroundColor: professionalTheme.gray[100],
                    borderColor: professionalTheme.primary,
                    color: professionalTheme.primary,
                  },
                  '&:disabled': {
                    color: professionalTheme.gray[400],
                    borderColor: professionalTheme.gray[300],
                  }
                }}
              >
                {isDraftSaving ? "Moving to Draft..." : "Move to Draft"}
              </Button>

              {activeStep === steps.length - 1 ? (
                <Button
                  variant="contained"
                  onClick={updateAd}
                  disabled={!isFormValid() || isApiLoader}
                  startIcon={<Publish />}
                  size="large"
                  sx={{
                     width: '250px',
                   borderRadius: 1,
                      px: 6,
                      py: 1.5,
                      fontWeight: 700,
                      textTransform: 'none',
                      fontSize: '16px',
                      fontFamily: professionalTheme.fontFamily,
                      backgroundColor: professionalTheme.primary,
                    '&:hover': {
                      backgroundColor: '#b8489e',
                    },
                    '&:disabled': {
                      backgroundColor: professionalTheme.gray[400],
                      color: professionalTheme.gray[600]
                    }
                  }}
                >
                 Publish Listing
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={isApiLoader}
                  endIcon={<ArrowForward />}
                  size="large"
                  sx={{
                   borderRadius: 1,
                      padding:'2px 6px',
                      fontWeight: 700,
                      width:'180px',
                      height:'55px',
                      textTransform: 'none',
                      fontSize: '16px',
                      fontFamily: professionalTheme.fontFamily,
                      backgroundColor: professionalTheme.primary,
                    '&:hover': {
                      backgroundColor: '#b8489e',
                    }
                  }}
                >
                  Next Step
                </Button>
              )}
            </Box>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default EditAdPage;